import type { ConversationFilter, ConversationListItem, ConversationSnippet } from "@esi/shared";
import { Archive, Globe, Lock, MessagesSquare, Search, SquarePen, Users } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { FilterChips } from "@/components/FilterChips";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { conversationPath, Link } from "../router.tsx";
import { ConversationRowMenu } from "./actions.tsx";
import { useConversationPages } from "./queries.ts";

/** Waits until typing pauses before searching (and never searches under two characters). */
export function useDebounced(value: string, ms = 250): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * The caller's conversations (spec `web-shell` "Project Conversations tab" and "Conversations
 * page"): a search field, the All / Mine / Shared with me / Archived filter (in a project, also
 * Shared with the project), then one hairline
 * container of rows with the title, last activity, sharing state and label summary as quiet
 * icon-and-text lines, and "Load more". `projectId` scopes it to a project; `showProject` names
 * each row's project (or "No project"). In a project, the empty list offers `newConversationHref`
 * (omitted when the project takes no conversation) as its primary action.
 */
export function ConversationList(props: {
  projectId?: string;
  showProject?: boolean;
  /** Shown beside the search field (the project's "New conversation"). */
  action?: ReactNode;
  /**
   * Where a new conversation in the project starts, for the empty list's primary action;
   * omitted when the project takes none (frozen), which the empty copy then says.
   */
  newConversationHref?: string;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [filter, setFilter] = useState<ConversationFilter>("all");
  // An API that predates the project share answers `400` to its filter: fall back to All.
  const [projectFilter, setProjectFilter] = useState(true);
  const settled = useDebounced(typed.trim());
  const q = settled.length >= 2 ? settled : undefined;
  const pages = useConversationPages({
    projectId: props.projectId,
    viaProject: props.projectId !== undefined,
    q,
    filter,
    limit: 25,
  });
  const unsupported =
    filter === "project" && pages.error instanceof ApiError && pages.error.status === 400;
  useEffect(() => {
    if (unsupported) {
      setProjectFilter(false);
      setFilter("all");
    }
  }, [unsupported]);
  const items = pages.data?.pages.flatMap((p) => p.items) ?? [];
  const inProject = props.projectId !== undefined;
  // Nothing started yet (no search, nothing filtered away but the caller's own).
  const none = !q && (filter === "all" || filter === "mine");
  const filters: { value: ConversationFilter; label: string }[] = [
    { value: "all", label: t("conversationList.filter.all") },
    { value: "mine", label: t("conversationList.filter.mine") },
    { value: "shared", label: t("conversationList.filter.shared") },
    ...(props.projectId !== undefined && projectFilter
      ? [{ value: "project" as const, label: t("conversationList.filter.project") }]
      : []),
    { value: "archived", label: t("conversationList.filter.archived") },
  ];

  return (
    <div data-esi-content="conversations" className="@container flex flex-col gap-3">
      <h2 className="sr-only">{t("nav.conversations")}</h2>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
            strokeWidth={1.75}
          />
          <Input
            type="search"
            value={typed}
            aria-label={t("conversationList.search")}
            placeholder={t("conversationList.search")}
            onChange={(e) => setTyped(e.currentTarget.value)}
            className="pl-9"
          />
        </div>
        {props.action}
      </div>
      <FilterChips
        label={t("conversationList.filter.label")}
        options={filters}
        value={filter}
        onChange={setFilter}
      />
      {pages.isError && !unsupported ? (
        <Notice tone="danger">{pages.error.message}</Notice>
      ) : pages.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={
            q
              ? Search
              : filter === "archived"
                ? Archive
                : filter === "project"
                  ? Users
                  : MessagesSquare
          }
          headingLevel={3}
          action={
            none && props.newConversationHref ? (
              <Button asChild size="sm">
                <Link href={props.newConversationHref}>
                  <SquarePen aria-hidden="true" strokeWidth={1.75} />
                  {t("projectHome.newConversation")}
                </Link>
              </Button>
            ) : null
          }
          title={
            q
              ? t("conversationList.empty.noMatchTitle")
              : filter === "archived"
                ? t("conversationList.empty.archivedTitle")
                : filter === "project"
                  ? t("conversationList.empty.projectTitle")
                  : t("conversationList.empty.noneTitle")
          }
        >
          {q
            ? t("conversationList.empty.noMatch")
            : filter === "archived"
              ? t("conversationList.empty.archived")
              : filter === "shared"
                ? t("conversationList.empty.shared")
                : filter === "project"
                  ? t("conversationList.empty.project")
                  : !inProject
                    ? t("conversationList.empty.none")
                    : props.newConversationHref
                      ? t("conversationList.empty.inProject")
                      : t("conversationList.empty.inProjectReadOnly")}
        </EmptyState>
      ) : (
        <>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
            {items.map((c) => (
              <ConversationRow key={c.id} conversation={c} showProject={props.showProject} />
            ))}
          </ul>
          {pages.hasNextPage ? (
            <Button
              variant="ghost"
              size="sm"
              className="self-center"
              disabled={pages.isFetchingNextPage}
              onClick={() => void pages.fetchNextPage()}
            >
              {pages.isFetchingNextPage ? t("app.loading") : t("conversationList.loadMore")}
            </Button>
          ) : null}
        </>
      )}
      <p role="status" className="sr-only">
        {pages.isSuccess && q ? t("search.count", { count: items.length }) : ""}
      </p>
    </div>
  );
}

function ConversationRow(props: { conversation: ConversationListItem; showProject?: boolean }) {
  const { conversation: c } = props;
  const { t } = useTranslation();
  const title = c.title ?? t("conversation.untitled");
  return (
    <li className="group/row relative flex items-start gap-3 px-4 py-3 transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-within:bg-surface-hover">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link
          href={conversationPath(c)}
          className="truncate text-sm font-medium text-fg after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
          title={title}
        >
          {title}
        </Link>
        {c.snippet ? <Snippet snippet={c.snippet} /> : null}
        <ConversationMeta conversation={c} showProject={props.showProject} />
      </div>
      {/* Above the row's stretched link, so the menu stays its own target. */}
      <div className="relative z-10 shrink-0">
        <ConversationRowMenu
          conversation={c}
          className="opacity-100 [@media(hover:hover)]:opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 data-[state=open]:opacity-100"
        />
      </div>
    </li>
  );
}

/** Last activity · project · sharing state · label summary, as one quiet wrapping line. */
export function ConversationMeta(props: {
  conversation: ConversationListItem;
  showProject?: boolean;
}) {
  const { conversation: c } = props;
  const { t } = useTranslation();
  const parts: ReactNode[] = [
    <time key="at" dateTime={c.updatedAt} className="tabular-nums">
      {formatDateTime(c.updatedAt)}
    </time>,
  ];
  if (props.showProject) {
    parts.push(<span key="project">{c.project?.name ?? t("conversationHeader.noProject")}</span>);
  }
  const sharer = c.sharedBy?.name ?? t("share.someone");
  parts.push(
    c.projectShare ? (
      // Shared with the project: marked with the people icon, by its creator for a viewer.
      <span key="sharing" className="inline-flex items-center gap-1">
        <Users aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {c.sharedBy
          ? t("conversationList.projectSharedBy", { name: sharer })
          : c.sharedWith
            ? t("conversationList.projectSharedAnd", { count: c.sharedWith })
            : t("conversationList.projectShared")}
      </span>
    ) : (
      <span key="sharing">
        {c.sharedBy
          ? t("conversationList.sharedBy", { name: sharer })
          : c.sharedWith
            ? t("conversationList.sharedWith", { count: c.sharedWith })
            : t("audience.onlyYou")}
      </span>
    ),
  );
  if (c.archivedAt) {
    parts.push(
      <span key="archived" className="inline-flex items-center gap-1">
        <Archive aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("conversationList.archived")}
      </span>,
    );
  }
  if (c.label?.level) {
    parts.push(
      <span key="level" className="inline-flex items-center gap-1">
        <Lock aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("conversationList.levelSources", { level: c.label.levelLabel ?? c.label.level })}
      </span>,
    );
  }
  if (c.label?.webResults) {
    parts.push(
      <span key="web" className="inline-flex items-center gap-1">
        <Globe aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("audience.webResults")}
      </span>,
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
      {parts.map((part, i) => (
        <Fragment key={(part as { key: string }).key}>
          {i > 0 ? <span aria-hidden="true">·</span> : null}
          {part}
        </Fragment>
      ))}
    </p>
  );
}

/** A matched question, its matches marked (selection wash, never colour alone: `<mark>`). */
export function Snippet({ snippet }: { snippet: ConversationSnippet }) {
  const out: ReactNode[] = [];
  let at = 0;
  for (const [start, end] of snippet.ranges) {
    if (start < at || end > snippet.text.length) {
      continue;
    }
    out.push(snippet.text.slice(at, start));
    out.push(
      <mark key={start} className="rounded-xs bg-mark px-px text-mark-fg">
        {snippet.text.slice(start, end)}
      </mark>,
    );
    at = end;
  }
  out.push(snippet.text.slice(at));
  return <p className="line-clamp-2 text-[13px] text-fg-secondary">{out}</p>;
}
